import React, { useState, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { confirmDialog } from "primereact/confirmdialog";
import useWatchlistStore, {
  DEFAULT_COLUMN_CONFIG,
} from "../../store/watchlistStore";
import { useWatchlistData } from "./hooks/useWatchlistData";
import WatchlistGrid from "./WatchlistGrid";
import PanelSymbolContextMenu from "./PanelSymbolContextMenu";
import ColumnPicker from "./ColumnPicker";
import { useGroupEmitter } from "../../hooks/useGroupLink";
import "./PanelBase.css";
import "./WatchlistPanel.css";
import { PanelToolbar, PanelToolbarHost } from "./PanelToolbar";
import useHostDocument from "~/hooks/useHostDocument";

const WatchlistPanel = ({
  initialToolbarPinned = true,
  onToolbarPinnedChange,
  claimFocus,
}) => {
  // PoC #popout: document the panel currently lives in (dropdown portal)
  const [hostDoc, hostProbe] = useHostDocument();
  const watchlists = useWatchlistStore((s) => s.watchlists);
  const activeWatchlist = useWatchlistStore((s) => s.activeWatchlist);
  const removeSymbol = useWatchlistStore((s) => s.removeSymbol);
  const setSymbolsOrder = useWatchlistStore((s) => s.setSymbolsOrder);
  const createWatchlist = useWatchlistStore((s) => s.createWatchlist);
  const deleteWatchlist = useWatchlistStore((s) => s.deleteWatchlist);
  const renameWatchlist = useWatchlistStore((s) => s.renameWatchlist);
  const setActiveWatchlist = useWatchlistStore((s) => s.setActiveWatchlist);
  const group = useWatchlistStore((s) => s.group);
  const savedColumnConfig = useWatchlistStore(
    (s) => s.columnConfigs[s.activeWatchlist],
  );
  const columnConfig = useMemo(() => {
    if (!savedColumnConfig) return DEFAULT_COLUMN_CONFIG;
    // Merge flags (e.g. fixed) from DEFAULT into saved config
    const defaultByKey = Object.fromEntries(
      DEFAULT_COLUMN_CONFIG.map((c) => [c.key, c]),
    );
    const merged = savedColumnConfig.map((c) => {
      const def = defaultByKey[c.key];
      return def?.fixed ? { ...c, fixed: true } : c;
    });
    // Append new columns from DEFAULT not in saved config
    const savedKeys = new Set(savedColumnConfig.map((c) => c.key));
    const newCols = DEFAULT_COLUMN_CONFIG.filter((c) => !savedKeys.has(c.key));
    return newCols.length ? [...merged, ...newCols] : merged;
  }, [savedColumnConfig]);
  const setColumnVisibility = useWatchlistStore((s) => s.setColumnVisibility);
  const setColumnOrder = useWatchlistStore((s) => s.setColumnOrder);
  const resetColumnConfig = useWatchlistStore((s) => s.resetColumnConfig);

  const symbols = useMemo(
    () => watchlists[activeWatchlist] || [],
    [watchlists, activeWatchlist],
  );
  const { rows } = useWatchlistData(symbols);

  // Group linking - emit symbol selections to linked charts
  const { emitSymbol } = useGroupEmitter(group);

  const handleSymbolSelect = useCallback(
    (symbol) => {
      emitSymbol(symbol);
    },
    [emitSymbol],
  );

  const [createDialogVisible, setCreateDialogVisible] = useState(false);
  const [renameDialogVisible, setRenameDialogVisible] = useState(false);
  const [dialogName, setDialogName] = useState("");
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [toolbarPinned, setToolbarPinned] = useState(initialToolbarPinned);
  const columnPickerBtnRef = useRef(null);
  const symbolMenuAreaRef = useRef(null);

  const watchlistOptions = useMemo(
    () => Object.keys(watchlists).map((name) => ({ label: name, value: name })),
    [watchlists],
  );

  const handleReorder = useCallback(
    (newOrder) => {
      setSymbolsOrder(newOrder);
    },
    [setSymbolsOrder],
  );

  const handleRemove = useCallback((sym) => removeSymbol(sym), [removeSymbol]);

  const handleCreateConfirm = useCallback(() => {
    const name = dialogName.trim();
    if (name) createWatchlist(name);
    setCreateDialogVisible(false);
    setDialogName("");
  }, [dialogName, createWatchlist]);

  const handleRenameConfirm = useCallback(() => {
    const name = dialogName.trim();
    if (name) renameWatchlist(activeWatchlist, name);
    setRenameDialogVisible(false);
    setDialogName("");
  }, [dialogName, renameWatchlist, activeWatchlist]);

  const handleDelete = useCallback(() => {
    confirmDialog({
      message: `Excluir a lista "${activeWatchlist}"?`,
      header: "Confirmar",
      icon: "pi pi-exclamation-triangle",
      accept: () => deleteWatchlist(activeWatchlist),
    });
  }, [activeWatchlist, deleteWatchlist]);

  const canDelete = Object.keys(watchlists).length > 1;
  const handleToolbarPinnedChange = useCallback(
    (pinned) => {
      setToolbarPinned(pinned);
      onToolbarPinnedChange?.(pinned);
    },
    [onToolbarPinnedChange],
  );

  return (
    <PanelToolbarHost
      pinned={toolbarPinned}
      onMouseDown={claimFocus}
      className="vertical-panel"
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
      }}
      contentStyle={{
        flex: "1 1 0",
        minHeight: 0,
        overflow: "hidden",
        padding: "4px",
      }}
      toolbar={
        <PanelToolbar
          pinned={toolbarPinned}
          onPinnedChange={handleToolbarPinnedChange}
          wrap
          pinDataEvoId="watchlistpanel-btn-toolbar-pin"
          right={
            <span ref={columnPickerBtnRef} style={{ display: "inline-flex" }}>
              <Button
                icon="pi pi-cog"
                size="small"
                text
                rounded
                title="Configurar colunas"
                onClick={() => setColumnPickerOpen((p) => !p)}
                style={{ width: 28, height: 28 }}
                data-evo-id="watchlistpanel-btn-columns"
              />
            </span>
          }
        >
          <Dropdown
            value={activeWatchlist}
            options={watchlistOptions}
            onChange={(e) => setActiveWatchlist(e.value)}
            appendTo={(hostDoc ?? document).body}
            style={{ minWidth: 120, fontSize: "12px", height: 28 }}
            pt={{
              input: { style: { padding: "4px 8px", lineHeight: "20px" } },
            }}
            data-evo-id="watchlistpanel-dropdown-select"
          />
          <Button
            icon="pi pi-plus"
            size="small"
            text
            rounded
            title="Nova lista"
            onClick={() => {
              setDialogName("");
              setCreateDialogVisible(true);
            }}
            style={{ width: 28, height: 28 }}
            data-evo-id="watchlistpanel-btn-create"
          />
          <Button
            icon="pi pi-pencil"
            size="small"
            text
            rounded
            title="Renomear lista"
            onClick={() => {
              setDialogName(activeWatchlist);
              setRenameDialogVisible(true);
            }}
            style={{ width: 28, height: 28 }}
            data-evo-id="watchlistpanel-btn-rename"
          />
          <Button
            icon="pi pi-trash"
            size="small"
            text
            rounded
            severity="danger"
            title="Excluir lista"
            disabled={!canDelete}
            onClick={handleDelete}
            style={{ width: 28, height: 28 }}
            data-evo-id="watchlistpanel-btn-delete"
          />
        </PanelToolbar>
      }
    >
      {hostProbe}
      {/* Wrapper mirrors .wlg-container's flex contract so the layout is
          unchanged; it hosts the right-click/long-press "Adicionar símbolo…"
          menu over the WHOLE grid area (empty state included). */}
      <div
        ref={symbolMenuAreaRef}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: "1 1 0",
          minHeight: 0,
        }}
      >
        <WatchlistGrid
          rows={rows}
          onReorder={handleReorder}
          onRemove={handleRemove}
          onSymbolSelect={handleSymbolSelect}
          columnConfig={columnConfig}
        />
      </div>
      <PanelSymbolContextMenu
        targetRef={symbolMenuAreaRef}
        panelType="grid"
        label="Adicionar símbolo…"
        icon="pi pi-plus"
      />

      <Dialog
        header="Nova Lista"
        visible={createDialogVisible}
        onHide={() => setCreateDialogVisible(false)}
        style={{ width: 300 }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <InputText
            value={dialogName}
            onChange={(e) => setDialogName(e.target.value)}
            placeholder="Nome da lista"
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleCreateConfirm()}
            data-evo-id="watchlistpanel-input-create-name"
          />
          <div
            style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}
          >
            <Button
              label="Cancelar"
              text
              onClick={() => setCreateDialogVisible(false)}
              data-evo-id="watchlistpanel-btn-create-cancel"
            />
            <Button
              label="Criar"
              onClick={handleCreateConfirm}
              data-evo-id="watchlistpanel-btn-create-confirm"
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        header="Renomear Lista"
        visible={renameDialogVisible}
        onHide={() => setRenameDialogVisible(false)}
        style={{ width: 300 }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <InputText
            value={dialogName}
            onChange={(e) => setDialogName(e.target.value)}
            placeholder="Novo nome"
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleRenameConfirm()}
            data-evo-id="watchlistpanel-input-rename-name"
          />
          <div
            style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}
          >
            <Button
              label="Cancelar"
              text
              onClick={() => setRenameDialogVisible(false)}
              data-evo-id="watchlistpanel-btn-rename-cancel"
            />
            <Button
              label="Salvar"
              onClick={handleRenameConfirm}
              data-evo-id="watchlistpanel-btn-rename-save"
            />
          </div>
        </div>
      </Dialog>

      {columnPickerOpen && (
        <ColumnPicker
          columnConfig={columnConfig}
          onToggle={setColumnVisibility}
          onReorder={setColumnOrder}
          onReset={() => {
            resetColumnConfig();
            setColumnPickerOpen(false);
          }}
          onClose={() => setColumnPickerOpen(false)}
          anchorRef={columnPickerBtnRef}
        />
      )}
    </PanelToolbarHost>
  );
};

WatchlistPanel.propTypes = {
  initialToolbarPinned: PropTypes.bool,
  onToolbarPinnedChange: PropTypes.func,
  claimFocus: PropTypes.func,
};

export default React.memo(WatchlistPanel);
